import {
  IMAGE_NODE_H,
  IMAGE_NODE_W,
  NODE_GAP_X,
  NODE_GAP_Y,
  canConnect,
  connect,
  createImageNode,
  isBoardAspect,
  isImage,
  nodeById,
  nodeSize,
  readingOrder,
  type Board,
  type BoardAspect,
  type BoardImageNode,
} from './canvasBoard';

const COMPOSE_VERB = /\b(?:gere|gerar|crie|criar|fa[cç]a|fazer)\b/i;
const COMPOSE_BASED = /\b(?:com base|basead[oa]|usando|a partir)\b/i;
const COMPOSE_PICTURE = /\b(?:imagem|foto|c[oô]modo|sala|quarto|ambiente|cen[aá]rio|local|lugar)\b/i;
const COMPOSE_SKIP = /\b(?:v[ií]deo|roteiro|keyframe|quadro[- ]chave|take|clipe|m[uú]sica|locu[cç][aã]o|trilha|narra[cç][aã]o)\b/i;
/** Furnish / decorate a place using objects from another photo — a new still, not an in-place edit. */
const COMPOSE_FURNISH = /\b(?:mobili(?:e|ar|a|ado|ada)|decor(?:e|ar|a|ado|ada))\b/i;
const COMPOSE_FROM_OTHER = /\b(?:segunda|2ª|outra|demais)\s+(?:imagem|foto)\b|\bcom os m[oó]veis\b|\bm[oó]veis\s+d(?:a|essa|esta|aquela|o)\b/i;

/**
 * A new still built from other stills.
 * "gere o cômodo mobiliado com base nos móveis dessa segunda imagem"
 * and "mobilie o local com os móveis da segunda imagem" both create a new node.
 * An explicit edit ("edite a primeira…") stays on the original node.
 */
export function isComposeImageRequest(text: string): boolean {
  const t = String(text || '').trim();
  if (!t || /^Respostas do formulário:/i.test(t)) return false;
  if (COMPOSE_SKIP.test(t)) return false;
  if (/\bedit(?:e|ar|a)\b/i.test(t) && !COMPOSE_VERB.test(t)) return false;
  if (COMPOSE_VERB.test(t) && COMPOSE_BASED.test(t) && COMPOSE_PICTURE.test(t)) return true;
  return COMPOSE_FURNISH.test(t) && COMPOSE_FROM_OTHER.test(t);
}

/** Last ratio the user wrote ("16:9", "4x3"). Absent means keep the source photo's shape. */
export function askedAspect(text: string): BoardAspect | undefined {
  const all = Array.from(String(text || '').matchAll(/\b(\d{1,2})\s*[:x×/]\s*(\d{1,2})\b/gi))
    .map((m) => `${m[1]}:${m[2]}`)
    .filter((a): a is BoardAspect => isBoardAspect(a));
  return all[all.length - 1];
}

function readyImage(n: BoardImageNode): boolean {
  return Boolean(n.phase.imageUrl) && n.phase.status === 'ready';
}

/** Left to right, then top to bottom. "Segunda imagem" is the second in this order. */
export function orderComposeSources(board: Board, ids: string[]): string[] {
  const nodes = ids
    .map((id) => nodeById(board, id))
    .filter((n): n is BoardImageNode => isImage(n) && readyImage(n));
  const unique = nodes.filter((n, i) => nodes.findIndex((m) => m.id === n.id) === i);
  return readingOrder(unique).map((n) => n.id);
}

/**
 * Sources for a new still. Two or more selected photos win.
 * One or none, and at most four photos on the board: use those photos.
 */
/** "a primeira" / "a segunda" names the first two photos in reading order, not every photo on the board. */
function namesOrderedPair(text: string): boolean {
  const t = String(text || '');
  if (/\bprimeira\b/i.test(t) && /\bsegunda\b/i.test(t)) return true;
  return COMPOSE_FURNISH.test(t) && COMPOSE_FROM_OTHER.test(t);
}

export function composeSourceIds(board: Board, selectedIds: string[], text = ''): string[] {
  const ready = board.nodes.filter(isImage).filter(readyImage);
  const selected = ready.filter((n) => selectedIds.includes(n.id));
  if (selected.length >= 2) return readingOrder(selected).map((n) => n.id);
  const ordered = readingOrder(ready);
  if (namesOrderedPair(text) && ordered.length >= 2) return ordered.slice(0, 2).map((n) => n.id);
  if (ordered.length >= 2 && ordered.length <= 4) return ordered.map((n) => n.id);
  return [];
}

export function composeLabel(instruction: string): string {
  const cleaned = String(instruction || '')
    .replace(/^(?:gere|gerar|crie|criar|fa[cç]a|fazer)\s+(?:uma\s+|um\s+|o\s+|a\s+)?/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  return (cleaned || 'Nova imagem').slice(0, 42);
}

/**
 * The user wants the image model to receive their words, not a rewrite.
 * Keep this pattern in sync with `wantsPromptUntouched` in supabase/functions/_shared/composePrompt.ts.
 */
const PROMPT_UNTOUCHED =
  /\b(?:n[aã]o\s+(?:modifiqu\w*|alter\w*|mud\w*|mex\w*|reescrev\w*|troc\w*)\s+(?:o\s+|no\s+|esse\s+|este\s+|meu\s+)?prompt|sem\s+(?:modificar|alterar|mudar|mexer\s+(?:n[oa]|em))\s+(?:o\s+)?prompt|prompt\s+(?:literal|exato|original|inalterad\w*|como\s+est[aá])|(?:use|mande|envie)\s+(?:o\s+)?prompt\s+(?:exato|literal|como\s+est[aá]|sem\s+(?:alterar|modificar))|don'?t\s+(?:modify|change|rewrite|alter)\s+(?:the\s+)?prompt|leave\s+(?:the\s+)?prompt\s+(?:as\s+is|unchanged|alone))\b/i;

export function wantsPromptUntouched(text: string): boolean {
  return PROMPT_UNTOUCHED.test(String(text || ''));
}

/** The user's words, with only the "don't rewrite the prompt" clause removed. */
export function verbatimImagePrompt(text: string): string {
  const raw = String(text || '').trim();
  const stripped = raw
    .replace(PROMPT_UNTOUCHED, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[\s,.;:]+/, '')
    .replace(/\s+([,.;:])/g, '$1')
    .trim();
  return stripped || raw;
}

function overlaps(board: Board, x: number, y: number): boolean {
  const pad = 12;
  return board.nodes.some((n) => {
    const size = nodeSize(n, board.aspectRatio);
    return x < n.x + size.w + pad && n.x < x + IMAGE_NODE_W + pad && y < n.y + size.h + pad && n.y < y + IMAGE_NODE_H + pad;
  });
}

/**
 * New image node to the right of the sources, with a reference edge from each source.
 * Those edges do not feed a video. The sources stay unchanged.
 */
export function placeComposedImage(
  board: Board,
  sourceIds: string[],
  instruction: string,
  prompt?: string,
): { board: Board; nodeId: string; sourceIds: string[] } | { error: string } {
  const ids = orderComposeSources(board, sourceIds);
  if (ids.length < 2) return { error: 'Selecione pelo menos duas imagens prontas.' };
  const sources = ids.map((id) => nodeById(board, id)).filter(isImage);
  const right = Math.max(...sources.map((n) => n.x + IMAGE_NODE_W));
  let x = right + NODE_GAP_X;
  let y = Math.round(sources.reduce((sum, n) => sum + n.y, 0) / sources.length);
  for (let i = 0; i < 8 && overlaps(board, x, y); i += 1) y += IMAGE_NODE_H + NODE_GAP_Y;
  const label = composeLabel(instruction);
  const node = createImageNode('', x, y, label);
  node.phase = {
    ...node.phase,
    status: 'pending',
    imageUrl: undefined,
    description_pt: instruction.trim(),
    still_prompt_en: (prompt || instruction).trim(),
    error: undefined,
  };
  let next: Board = { ...board, nodes: [...board.nodes, node] };
  for (const id of ids) {
    const reason = canConnect(next, id, node.id, 'ref');
    if (reason) return { error: reason };
    next = connect(next, id, node.id, 'ref');
  }
  return { board: next, nodeId: node.id, sourceIds: ids };
}
